import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import FolderIcon from "@mui/icons-material/Folder";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { AppContext } from "../../services/translationContext";
import Grid from "@mui/material/Grid";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import BackspaceIcon from '@mui/icons-material/Backspace';
import Badge from "@mui/material/Badge";
import FilterListIcon from "@mui/icons-material/FilterList";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import { formatDate } from "../../services/FormattedDate";
import { useParams } from "react-router-dom";
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArticlesTable from "./articles-table";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import TableRowsIcon from '@mui/icons-material/TableRows';

// ─── Costanti ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  published: { label: "Pubblicato", color: "success" },
  approved:  { label: "Approvato",  color: "info"    },
  draft:     { label: "Bozza",      color: "warning" },
  released:  { label: "Released",   color: "default" },
};

const STATUS_FLOW = ["draft", "approved", "published"];

const STATUS_TRANSITIONS = {
  draft:     ["approved"],
  approved:  ["published", "draft"],
  published: ["draft"]
};

const LANGS = ["it", "en"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ["draft", "approved", "published"];

function LangCell({ article, lang, onEdit, onHistory, onStatusChange, articleId, categoryId, subcategoryId, onDelete }) {
  const [anchor, setAnchor] = useState(null);
  const [notesOpen, setNotesOpen]   = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [notes, setNotes] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { lang: langFromUrl } = useParams();

  const canGoTo = (current, next) => {
    return STATUS_TRANSITIONS[current]?.includes(next);
  };

  const compareVersions = (a, b) => {
    const pa = String(a).split(".").map(Number);
    const pb = String(b).split(".").map(Number);
    const len = Math.max(pa.length, pb.length);

    for (let i = 0; i < len; i++) {
      const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
      if (diff !== 0) return diff;
    }
    return 0;
  };

  const lastPublishedVersion = article?.lastPublishedVersion?.versionNumber;

  const isOutdated =
    lastPublishedVersion &&
    compareVersions(article.version, lastPublishedVersion) > 0;

  if (!article) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" color="text.disabled" fontStyle="italic">
          Non presente
        </Typography>
        <Tooltip title={`Crea versione ${lang.toUpperCase()}`}>
          <IconButton size="small" color="primary" onClick={() => onEdit(null, lang, articleId, categoryId, subcategoryId)}>
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", textAlign: 'left' }}>
        <Typography variant="caption" color="text.secondary" component="span" sx={{ fontWeight: 600 }}>{article.title}</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>

        {/* Chip stato cliccabile */}
        <Tooltip title="Cambia stato">
          <Chip
            label={STATUS_CONFIG[article.status]?.label ?? article.status}
            color={STATUS_CONFIG[article.status]?.color ?? "default"}
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ fontWeight: 600, cursor: "pointer", fontSize: '0.7rem' }}
          />
        </Tooltip>

        {/* Menu a tendina stati */}
        {/* <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem
              key={s}
              selected={article.status === s}
              onClick={() => {
                setAnchor(null);
                if (s !== article.status) {
                  setPendingStatus(s);
                  setNotes("");
                  setNotesOpen(true);
                }
              }}
            >
              <Chip
                label={STATUS_CONFIG[s]?.label ?? s}
                color={STATUS_CONFIG[s]?.color ?? "default"}
                size="small"
                sx={{ fontWeight: 600, minWidth: 90, pointerEvents: "none" }}
              />
            </MenuItem>
          ))}
        </Menu> */}

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
        >
          {STATUS_FLOW.map((status, index) => {
            const isCurrent = article.status === status;
            const isAllowed = canGoTo(article.status, status);

            return (
              <Box key={status}>
                <MenuItem
                  disabled={!isAllowed && !isCurrent}
                  // onClick={() => {
                  //   if (!isAllowed || isCurrent) return;
                  //   onStatusChange(status);
                  //   setAnchor(null);
                  // }}
                  onClick={() => {
                    setAnchor(null);
                    if (status !== article.status) {
                      setPendingStatus(status);
                      setNotes("");
                      setNotesOpen(true);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    opacity: isAllowed || isCurrent ? 1 : 0.4
                  }}
                >
                  <Chip
                    label={STATUS_CONFIG[status].label}
                    color={STATUS_CONFIG[status].color}
                    size="small"
                    variant={isCurrent ? "filled" : "outlined"}
                    sx={{fontWeight: isCurrent ? 600 : 500}}
                  />
                </MenuItem>

                {index < STATUS_FLOW.length - 1 && (
                  <Box sx={{ px: 3, px: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    {/* <Typography variant="caption" color="text.disabled" sx={{fontSize: '0.8rem', fontWeight: 600}}>
                      ↓
                    </Typography> */}
                    <KeyboardDoubleArrowDownIcon fontSize="inherit" sx={{color:'#a1a1a1'}}/>
                  </Box>
                )}
              </Box>
            );
          })}
        </Menu>

        <Dialog
          open={notesOpen}
          onClose={() => setNotesOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="body1" sx={{fontWeight: 600}}>{article.title} - v{article.version}</Typography>
            <Typography variant="caption" color="text.secondary">Cambio stato</Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Chip
                label={STATUS_CONFIG[article.status]?.label}
                color={STATUS_CONFIG[article.status]?.color}
                size="small"
                variant="outlined"
              />
              <Typography variant="body2" color="text.secondary">→</Typography>
              <Chip
                label={STATUS_CONFIG[pendingStatus]?.label}
                color={STATUS_CONFIG[pendingStatus]?.color}
                size="small"
              />
            </Box>
            <TextField
              label="Note (opzionale)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder={`Stato cambiato in ${STATUS_CONFIG[pendingStatus]?.label ?? pendingStatus}...`}
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotesOpen(false)} color="inherit">
              Annulla
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setNotesOpen(false);
                onStatusChange(article, lang, pendingStatus, notes || `Stato cambiato in ${pendingStatus}`);
              }}
            >
              Conferma
            </Button>
          </DialogActions>
        </Dialog>

        {/* <Typography variant="caption" color="text.secondary">
          v{article.version} · {article.lastUpdated ?? "—"}
        </Typography> */}

        {/* <Typography variant="caption" color="text.secondary">
          v{article.version} · {article.lastUpdated ? formatDate(article.lastUpdated, langFromUrl) : "—"}
          {article.lastPublishedVersion && 
          article.lastPublishedVersion.versionNumber < article.version && (
            <Chip
              label={`online v${article.lastPublishedVersion.versionNumber}`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ ml: 1, height: 16, fontSize: "0.6rem" }}
            />
          )}
        </Typography> */}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {/* Testo */}
          <Typography variant="caption" color="text.secondary" component="span" sx={{ fontSize: '0.7rem' }}>
            v{article.version} ·{" "}
            {article.lastUpdated
              ? formatDate(article.lastUpdated, langFromUrl)
              : "—"}
          </Typography>

          {/* Chip versione online */}
          {isOutdated && (
            <Chip
              label={`online v${lastPublishedVersion}`}
              size="small"
              color="info"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem" }}
            />
          )}
        </Box>

        <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
          <Tooltip title="Modifica">
            <IconButton size="small" onClick={() => onEdit(article, lang)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Storico versioni">
            <IconButton size="small" onClick={() => onHistory(article, lang)}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Elimina">
            <IconButton size="small" color="error" onClick={() => setConfirmDelete(true)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Dialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Conferma eliminazione</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Eliminare definitivamente la versione <strong>{lang.toUpperCase()}</strong> dell'articolo <strong>{article.title}</strong> (id: {articleId})?
              </Typography>
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Questa operazione è irreversibile ed eliminerà tutto lo storico versioni per questa lingua.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDelete(false)} color="inherit">
                Annulla
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setConfirmDelete(false);
                  onDelete(articleId, lang);
                }}
              >
                Elimina
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Riga articolo ────────────────────────────────────────────────────────────

function ArticleRow({ articleId, articlesByLang, onEdit, onHistory, onStatusChange, onDelete }) {
  const it = articlesByLang.it;
  const en = articlesByLang.en;

  // Titolo: preferisce IT, poi EN
  const title = it?.title ?? en?.title ?? articleId;

  return (
    <TableRow hover sx={{ "&:last-child td": { borderBottom: 0 }}}>
      <TableCell sx={{ pl: 4, border: 0, borderBottom: '1px solid #e0e0e0' }}>
        {/* <Typography variant="body2" fontWeight={500}>
          {title}
        </Typography> */}
        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5, fontSize: "0.8rem" }}>
          {articleId}
        </Typography>
      </TableCell>

      {LANGS.map((lang) => (
        <TableCell key={lang} sx={{ minWidth: 220, border: 0, borderBottom: '1px solid #e0e0e0' }}>
          <LangCell
            article={articlesByLang[lang] ?? null}
            lang={lang}
            articleId={articleId}
            categoryId={articlesByLang["it"]?.category ?? articlesByLang["en"]?.category ?? ""}
            subcategoryId={articlesByLang["it"]?.subcategory ?? articlesByLang["en"]?.subcategory ?? ""}
            onEdit={(article, l, existingId, catId, subId) => onEdit(articleId, article, l, existingId, catId, subId)}
            onHistory={(article, l) => onHistory(articleId, article, l)}
            onStatusChange={(article, l, newStatus, notes) => onStatusChange(articleId, article, l, newStatus, notes)}
            onDelete={onDelete}
          />
        </TableCell>
      ))}
    </TableRow>
  );
}

// ─── Blocco sottocategoria ────────────────────────────────────────────────────

function SubcategoryBlock({ categoryId, subcategoryId, categoryLabelIT, categoryLabelEN, subcategoryLabelIT, subcategoryLabelEN, articles, onEdit, onHistory, onAddArticle, onStatusChange, isArticleVisible, onDelete }) {
  const [open, setOpen] = useState(true);
  const { services: {TranslationsService} } = useContext(AppContext);

  // Raggruppa per articleId → { it: {...}, en: {...} }
  const byId = {};
  // for (const article of articles) {
  //   if (!byId[article.id]) byId[article.id] = {};
  //   byId[article.id][article.lang] = article;
  // }

  for (const article of articles) {
    if (!byId[article.id]) byId[article.id] = {};

    let enrichedArticle = { ...article };

    if (article.lang === "it") {
      enrichedArticle.categoryLabel = categoryLabelIT;
      enrichedArticle.subcategoryLabel = subcategoryLabelIT;
    }
    if (article.lang === "en") {
      enrichedArticle.categoryLabel = categoryLabelEN;
      enrichedArticle.subcategoryLabel = subcategoryLabelEN;
    }
    byId[article.id][article.lang] = enrichedArticle;
  }

  const articleIds = Object.keys(byId);
  const visibleIds = articleIds.filter(id => isArticleVisible(byId[id]));
  if (articleIds.length > 0 && visibleIds.length === 0) return null;

  return (
    <Box sx={{ mb: 1 }}>
      {/* Header sottocategoria */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 0.75,
          // bgcolor: "grey.200",
          bgcolor: "#fff4ce",
          borderRadius: 1,
          cursor: "pointer",
          "&:hover": { bgcolor: "#ffeeaf" },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <IconButton size="small" sx={{ p: 0 }}>
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </IconButton>
        <Typography variant="body2" fontWeight={600} color="#0d0d0d" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.7rem" }}>
          {/* {getLabel(subcategoryLabel)} */}
          {subcategoryLabelIT} 🇮🇹 - {subcategoryLabelEN} 🇬🇧
        </Typography>
        <Typography variant="body3" fontWeight={500} color="#0d0d0d" sx={{ letterSpacing: 0.5, fontSize: "0.6rem" }}>
          ID: {subcategoryId}
        </Typography>
        <Chip label={articleIds.length} size="small" icon={<ArticleOutlinedIcon sx={{ fontSize: "0.75rem !important" }}/>} sx={{ ml: 0.5, height: 18, fontSize: "0.65rem" }} />
        <Box sx={{ ml: "auto" }}>
          <Tooltip title="Nuovo articolo in questa sottocategoria">
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => { e.stopPropagation(); debugger; onAddArticle(categoryId, subcategoryId); }}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabella articoli */}
      <Collapse in={open}>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5, mb: 1, borderRadius: 1 }}>
          <Table size="small" sx={{border:0}}>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell sx={{ pl: 4, fontWeight: 700, fontSize: "0.75rem", color: "text.secondary", width: '33.33%', border: 0, borderBottom: '1px solid #e0e0e0' }}>
                  ID Articolo
                </TableCell>
                {LANGS.map((lang) => (
                  <TableCell key={lang} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary", minWidth: 220, width: '33.33%', border: 0, borderBottom: '1px solid #e0e0e0' }}>
                    {lang.toUpperCase()}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleIds.map((id) => (
              // {articleIds.map((id) => (
                <ArticleRow
                  key={id}
                  articleId={id}
                  articlesByLang={byId[id]}
                  onEdit={onEdit}
                  onHistory={onHistory}
                  onStatusChange={(articleId, article, l, newStatus, notes) =>
                    onStatusChange(articleId, article, l, newStatus, notes)
                  }
                  isArticleVisible={isArticleVisible}
                  onDelete={onDelete}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Box>
  );
}

// ─── Blocco categoria ─────────────────────────────────────────────────────────

function CategoryBlock({ categoryId, categoryLabelIT, categoryLabelEN, subcategories, articles, onEdit, onHistory, onAddArticle, onStatusChange, isArticleVisible, onDelete }) {
  const [open, setOpen] = useState(true);
  const { services: {TranslationsService} } = useContext(AppContext);

  const byId = {};
  for (const article of articles) {
    if (!byId[article.id]) byId[article.id] = {};
    byId[article.id][article.lang] = article;
  }

  const hasArticles = articles.length > 0;
  const hasVisibleArticles = Object.keys(byId).some(id => isArticleVisible(byId[id]));
  if (hasArticles && !hasVisibleArticles) return null;

  const totalArticles = Object.values(
    articles.reduce((acc, a) => { acc[a.id] = true; return acc; }, {})
  ).length;

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
      {/* Header categoria */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.25,
          // bgcolor: open ? "primary.main" : "primary.light",
          bgcolor: open ? "#fece2f" : "#ffffff",
          cursor: "pointer",
          transition: "background 0.2s",
          "&:hover": { bgcolor: "#f8be01" },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        {open
          ? <FolderOpenIcon sx={{ color: "#090909", fontSize: 20 }} />
          : <FolderIcon sx={{ color: "#090909", fontSize: 20 }} />
        }
        <Typography variant="subtitle2" fontWeight={700} color="#090909" sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
          {/* {getLabel(categoryLabelIT)} */}
          {categoryLabelIT} 🇮🇹 - {categoryLabelEN} 🇬🇧
        </Typography>
        <Typography variant="subtitle2" fontWeight={500} color="#090909" sx={{ letterSpacing: 0.8, fontSize: '0.6rem' }}>
          ID: {categoryId}
        </Typography>
        <Chip
          // label={`${totalArticles} articol${totalArticles > 1 ? 'i':'o'} `}
          label={`${totalArticles}`}
          // 
          icon={<ArticleOutlinedIcon />} 
          size="small"
          sx={{ bgcolor: "#ffffff94", color: "#090909", fontWeight: 600, height: 20, fontSize: "0.65rem" }}
        />
        <IconButton size="small" sx={{ ml: "auto", p: 0 }}>
          <ExpandMoreIcon
            sx={{
              color: "#090909",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </IconButton>
      </Box>

      {/* Sottocategorie */}
      <Collapse in={open}>
        <Box sx={{ p: 1.5 }}>
          {subcategories.map((sub) => {
            const subArticles = articles.filter((a) => a.subcategory === sub.id);
            return (
              <SubcategoryBlock
                key={sub.id}
                categoryId={categoryId}
                subcategoryId={sub.id}
                categoryLabelIT={categoryLabelIT}
                categoryLabelEN={categoryLabelEN}
                subcategoryLabelIT={sub.labelIT}
                subcategoryLabelEN={sub.labelEN}
                labelEditorHeader={`${categoryLabelIT}$sub.labelEN`}
                articles={subArticles}
                onEdit={onEdit}
                onHistory={onHistory}
                onAddArticle={(categoryId, subId) => onAddArticle(categoryId, subId)}
                onStatusChange={onStatusChange}
                isArticleVisible={isArticleVisible}
                onDelete={onDelete}
              />
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ─── Componente principale ────────────────────────────────────────────────────

/**
 * KbAdminList
 *
 * Props:
 *   pathUrl   {string}   — base URL (es. "https://www.sydea.com")
 *   onEdit    {function} — (articleId, article|null, lang) => void
 *   onHistory {function} — (articleId, article, lang) => void
 *   onAdd     {function} — (categoryId, subcategoryId) => void
 */
export default function KbAdminList({ pathUrl, onEdit, onHistory, onAdd, onStatusChange, onManageCategories, onIndexLoaded, onDelete }) {
  const [index, setIndex]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [filterStatus, setFilterStatus]   = useState([]);
  const [filterLang, setFilterLang]       = useState([]);
  const [filterGroup, setFilterGroup]     = useState([]);
  const [filterAuthor, setFilterAuthor]   = useState([]);
  const [filterGodMode, setFilterGodMode] = useState(null); // null=tutti, true=solo godMode, false=no godMode
  const [filterSearch, setFilterSearch]   = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [modView, setModView] = useState('grid');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${pathUrl}/static/knowledge-base/index.json?_=${Date.now()}`,
          { cache: "no-store" }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setIndex(data);
        if (onIndexLoaded) onIndexLoaded(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [pathUrl]);

  const handleModView = (event, newMod) => {
    setModView(newMod);
  };


  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
          Caricamento knowledge base...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography color="error">Errore nel caricamento: {error}</Typography>
      </Box>
    );
  }

  if (!index) return null;

  // Ricava lista autori unici dall'index
  const authorOptions = [...new Set(
    (index?.articles ?? []).map(a => a.author).filter(Boolean)
  )].sort();

  // Applica filtri agli articoli
  const isArticleVisible = (articlesByLang) => {
    const articles = Object.values(articlesByLang);

    if (filterSearch.trim()) {
      const q = filterSearch.toLowerCase();
      const matches = articles.some(a =>
        a.id?.toLowerCase().includes(q) ||
        a.title?.toLowerCase().includes(q) ||
        a.category?.toLowerCase().includes(q) ||
        a.subcategory?.toLowerCase().includes(q) ||
        a.title?.toLowerCase().includes(q) ||
        a.tags?.some(t => t.toLowerCase().includes(q))
      );
      if (!matches) return false;
    }

    if (filterStatus.length > 0) {
      if (!articles.some(a => filterStatus.includes(a.status))) return false;
    }

    if (filterLang.length > 0) {
      if (!articles.some(a => filterLang.includes(a.lang))) return false;
    }

    if (filterGroup.length > 0) {
      if (!articles.some(a => a.groups?.some(g => filterGroup.includes(g)))) return false;
    }

    if (filterAuthor.length > 0) {
      if (!articles.some(a => filterAuthor.includes(a.author))) return false;
    }

    if (filterGodMode !== null) {
      if (!articles.some(a => a.godMode === filterGodMode)) return false;
    }

    return true;
  };

  const activeFiltersCount =
    (filterSearch ? 1 : 0) +
    filterStatus.length +
    filterLang.length +
    filterGroup.length +
    filterAuthor.length +
    (filterGodMode !== null ? 1 : 0);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }} className='row'>
        <Box className='col-xs-12 col-md-6'>
          <Typography variant="h5" fontWeight={700}>
            Knowledge Base
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {index.categories.length} categorie · {index.articles.length} documenti totali (IT + EN)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "end",
          "@media (max-width:600px)": {
            mt: 1,
          } }} className='col-xs-12 col-md-6'>
          <Badge badgeContent={activeFiltersCount} color="error">
            <Button
              variant={showFilters ? "contained" : "outlined"}
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(v => !v)}
              sx={{ textTransform: "none" }}
            >
              Filtri
            </Button>
          </Badge>
          <Button
            variant="outlined"
            startIcon={<FolderOpenIcon />}
            onClick={onManageCategories}
            size="small"
            sx={{ textTransform: "none" }}
          >
            Gestisci categorie
          </Button>

          <ToggleButtonGroup
            value={modView}
            exclusive
            onChange={handleModView}
            aria-label="mod view"
            size="small"
            color="primary"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewComfyIcon />
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <TableRowsIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Barra filtri */}
      <Collapse in={showFilters}>
        <Paper variant="clear" sx={{ mb: 2, borderRadius: 2, backgroundColor: '#f6f6f6' }}>
          {/* <Grid container spacing={2} alignItems="center"> */}
          <div className="container">
            <div className="row">

            {/* Ricerca testuale */}
            <div className="col-xs-12 col-md-3">
              <TextField
                placeholder="Cerca articolo"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />,
                  endAdornment: filterSearch && (
                    <IconButton size="small" onClick={() => setFilterSearch("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </div>

            {/* Stato */}
            <div className="col-xs-12 col-md-2">
              <Autocomplete
                multiple
                options={["draft", "approved", "published"]}
                value={filterStatus}
                onChange={(_, v) => setFilterStatus(v)}
                getOptionLabel={(o) => STATUS_CONFIG[o]?.label ?? o}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={STATUS_CONFIG[option]?.label ?? option}
                      size="small"
                      color={STATUS_CONFIG[option]?.color ?? "default"}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Stato" size="small" />
                )}
              />
            </div>

            {/* Lingua */}
            <div className="col-xs-12 col-md-2">
              <Autocomplete
                multiple
                options={["it", "en"]}
                value={filterLang}
                onChange={(_, v) => setFilterLang(v)}
                getOptionLabel={(o) => o.toUpperCase()}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.toUpperCase()} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Lingua" size="small" />
                )}
              />
            </div>

            {/* Gruppo */}
            <div className="col-xs-12 col-md-2">
              <Autocomplete
                multiple
                options={["Bologna", "Napoli", "Skopje"]}
                value={filterGroup}
                onChange={(_, v) => setFilterGroup(v)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Gruppo" size="small" />
                )}
              />
            </div>

            {/* Autore */}
            <div className="col-xs-12 col-md-2">
              <Autocomplete
                multiple
                options={authorOptions}
                value={filterAuthor}
                onChange={(_, v) => setFilterAuthor(v)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} size="small" {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Autore" size="small" />
                )}
              />
            </div>

            {/* God Mode */}
            {/* <Grid item xs={6} md={1.5}>
              <Autocomplete
                options={[true, false]}
                value={filterGodMode}
                onChange={(_, v) => setFilterGodMode(v)}
                getOptionLabel={(o) => o === true ? "Solo admin" : "Tutti"}
                renderInput={(params) => (
                  <TextField {...params} label="God Mode" size="small" />
                )}
              />
            </Grid> */}

            {/* Reset filtri */}
            <div className="col-xs-12 col-md-1">
              <IconButton 
                size="small"
                onClick={() => {
                  setFilterSearch("");
                  setFilterStatus([]);
                  setFilterLang([]);
                  setFilterGroup([]);
                  setFilterAuthor([]);
                  setFilterGodMode(null);
                }}
                disabled={
                  !filterSearch &&
                  filterStatus.length === 0 &&
                  filterLang.length === 0 &&
                  filterGroup.length === 0 &&
                  filterAuthor.length === 0 &&
                  filterGodMode === null
                }
              >
                <BackspaceIcon fontSize="small" />
              </IconButton>
            </div>
            </div>
          </div>
        </Paper>
      </Collapse>

      {
        modView === 'table' ?
        (
          <ArticlesTable articles={index?.articles ?? []} onEdit={onEdit} />
        )
        :
        (
          index.categories.map((cat) => {
              const catArticles = index.articles.filter((a) => a.category === cat.id);
              return (
                <CategoryBlock
                  key={cat.id}
                  categoryId={cat.id}
                  categoryLabelIT={cat.labelIT}
                  categoryLabelEN={cat.labelEN}
                  subcategories={cat.subcategories}
                  articles={catArticles}
                  onEdit={onEdit ?? (() => {})}
                  onHistory={onHistory ?? (() => {})}
                  onAddArticle={onAdd ?? (() => {})}
                  onStatusChange={onStatusChange ?? (() => {})}
                  isArticleVisible={isArticleVisible}
                  onDelete={onDelete}
                />
              );
            })
        )
      }

    </Box>
  );
}